// AcceptTerms.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Card,
    Checkbox,
    Form,
    Typography,
    Row,
    Col,
    Space,
    Divider,
    Input,
    Flex,
} from 'antd';
import {
    SafetyCertificateOutlined,
    FileTextOutlined,
    ArrowRightOutlined,
    MoonOutlined,
    SunOutlined
} from '@ant-design/icons';
import { facebookRegister } from './AcceptTermsService';
import { saveAuthData } from '../../Config/ApiConfig';
import { ReactComponent as LogoWide } from '../../Images/logo-wide.svg';
import { RTL_LANGUAGES } from '../../Constants/language';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { basename } from '../../Constants/env';
import LanguageSwitcher from '../Shared/LanguageSwitcher/LanguageSwitcher';
import { useErrorHandler } from '../../Hooks/useErrorHandler';

const { Title, Text } = Typography;

const AcceptTermsPage = () => {
    const { t, i18n } = useTranslation();
    const { isDarkMode, toggleDarkMode } = useTheme();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user, access_token } = state || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [formValid, setFormValid] = useState(false);
    const { handleError } = useErrorHandler();

    const acceptPrivacy = Form.useWatch('privacy_policy_accepted', form);
    const acceptTerms = Form.useWatch('term_of_service_accepted', form);
    const email = Form.useWatch('email', form);
    const first_name = Form.useWatch('first_name', form);
    const last_name = Form.useWatch('last_name', form);
    const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

    useEffect(() => {
        setFormValid(acceptPrivacy && email && first_name && last_name);
    }, [acceptPrivacy, email, first_name, last_name]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const { privacy_policy_accepted, ...userData } = values;
            const registrationData = {
                ...userData,
                privacy_policy_accepted,
                term_of_service_accepted: privacy_policy_accepted,
                access_token
            };
            const { data } = await facebookRegister(registrationData);
            saveAuthData(data);
            navigate('/');
        } catch (error) {
            handleError(error, form);
        } finally {
            setLoading(false);
        }
    };

    if (!state) {
        navigate('/404');
        return <></>;
    }
    return (
        <div style={{
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '100vh',
            background: isDarkMode ? 'linear-gradient(135deg, #070605 0%, #161310 100%)' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            padding: '24px',
            boxSizing: 'border-box'
        }}>
            <Flex 
                flex={1} 
                align="center" 
                justify="center"
            >
                <Card
                    style={{
                        width: '100%',
                        maxWidth: '800px',
                        borderRadius: '16px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                        border: 'none',
                        boxSizing: 'border-box',
                    }}
                    bodyStyle={{ padding: 40 }}
                >
                    <Row gutter={[40, 24]} align="middle">
                        <Col xs={24} md={12}>
                            <LogoWide width="200px" height="100px" />
                            <Title level={2} style={{ marginBottom: '8px' }}>
                                {t('almost_there')}
                            </Title>
                            <Text type="secondary" style={{ fontSize: '16px' }}>
                                {t('complete_registration_message')}
                            </Text>

                            <Divider style={{ margin: '24px 0' }} />

                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                <div>
                                    <Title level={5} style={{ marginBottom: '8px' }}>
                                        <SafetyCertificateOutlined /> {t('data_protection')}
                                    </Title>
                                    <Text type="secondary">
                                        {t('data_protection_message')}
                                    </Text>
                                </div>

                                <div>
                                    <Title level={5} style={{ marginBottom: '8px' }}>
                                        <FileTextOutlined /> {t('transparent_policies')}
                                    </Title>
                                    <Text type="secondary">
                                        {t('transparent_policies_message')}
                                    </Text>
                                </div>
                            </Space>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form
                                form={form}
                                onFinish={handleSubmit}
                                initialValues={{
                                    acceptPrivacy: false,
                                    acceptTerms: false,
                                    email: user.email,
                                    first_name: user.first_name,
                                    last_name: user.last_name
                                }}
                                layout="vertical"
                            >
                                <Form.Item
                                    name="email"
                                    label={t('email')}
                                    style={{ marginBottom: 4 }}
                                    rules={[{ required: true, message: t('please_input_the_email') }]}
                                >
                                    <Input placeholder={t('email')} />
                                </Form.Item>
                                <Form.Item
                                    name="first_name"
                                    label={t('first_name')}
                                    style={{ marginBottom: 4 }}
                                    rules={[{ required: true, message: t('please_input_the_first_name') }]}
                                >
                                    <Input placeholder={t('first_name')} />
                                </Form.Item>
                                <Form.Item
                                    name="last_name"
                                    label={t('last_name')}
                                    style={{ marginBottom: 4 }}
                                    rules={[{ required: true, message: t('please_input_the_last_name') }]}
                                >
                                    <Input placeholder={t('last_name')} />
                                </Form.Item>
                                <Form.Item
                                    name="privacy_policy_accepted"
                                    valuePropName="checked"
                                    style={{ marginBottom: 0 }}
                                    rules={[{
                                        required: true,
                                        message: t('you_must_accept_the_privacy_policy_and_terms')
                                    }]}
                                >
                                    <Checkbox>
                                        <Text strong>
                                            {t('i_agree_to_the')}{' '}
                                            <a
                                                href={basename + "/privacy.html"}
                                                target="_blank"
                                                style={{ color: '#1890ff' }} rel="noreferrer"
                                            >
                                                {t('privacy_policy')}
                                            </a>
                                            {' '}{t('and')}{' '}
                                            <a
                                                href={basename + "/terms.html"}
                                                target="_blank"
                                                style={{ color: '#1890ff' }} rel="noreferrer"
                                            >
                                                {t('terms_of_service')}
                                            </a>
                                        </Text>
                                    </Checkbox>
                                </Form.Item>

                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    block
                                    size="large"
                                    disabled={!formValid || loading}
                                    icon={<ArrowRightOutlined />}
                                    style={{
                                        height: '48px',
                                        borderRadius: '8px',
                                        background: '#4A90E2',
                                        borderColor: '#4A90E2',
                                        fontSize: '16px',
                                        marginTop: '24px'
                                    }}
                                >
                                    {t('complete_registration')}
                                </Button>
                            </Form>
                        </Col>
                    </Row>
                </Card>
            </Flex>
            
            {/* Footer with language and dark mode toggles */}
            <Flex
                justify="flex-end"
                style={{
                    width: '100%',
                    padding: '10px 20px',
                    boxSizing: 'border-box',
                    marginTop: '20px'
                }}
            >
                <Space size="middle">
                    <LanguageSwitcher />
                    <div
                        onClick={toggleDarkMode}
                        style={{
                            cursor: 'pointer',
                            fontSize: 20,
                            color: isDarkMode ? '#fff' : '#000',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {isDarkMode ? <MoonOutlined /> : <SunOutlined />}
                    </div>
                </Space>
            </Flex>
        </div>
    );
};

export default AcceptTermsPage;
